import type { CSSProperties, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { gsap, prefersReducedMotion, registerGsap } from '@/lib/gsap'

interface MarqueeProps {
    /** Seconds for one full cycle (Webflow "Move Clients" used 20s). */
    duration?: number
    className?: string
    style?: CSSProperties
    children?: ReactNode
}

/**
 * Infinite horizontal marquee. The wrapper is the clipping mask (overflow
 * hidden) and must contain two identical content sets as its direct children;
 * both slide left by 100% of their own width so the loop is seamless.
 * Recreates Webflow action list a-70 ("Move Clients").
 */
export const Marquee = ({ duration = 20, className, style, children }: MarqueeProps) => {
    const ref = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        registerGsap()
        const el = ref.current
        if (!el || !el.children.length) return
        if (prefersReducedMotion()) return

        const targets = Array.from(el.children)

        const ctx = gsap.context(() => {
            gsap.to(targets, { xPercent: -100, duration, ease: 'none', repeat: -1 })
        }, el)

        return () => ctx.revert()
    }, [duration])

    return (
        <div ref={ref} className={className} style={style}>
            {children}
        </div>
    )
}
